// src/l1packs/russian.js
// Source: "Norwegian speech sounds compared to Russian" (Olaf Husby, NTNU NoW)
//
// Normalizations:
// - Use canonical IPA /ɡ/ (PDF table writes /g/)
// - Use canonical IPA /oː/ (PDF table writes /o:/)
// - PDF sometimes uses "+~" (between + and ~); we map that to "~" because UI supports only +/~/-.
//
// Visualization tags vocabulary (consonants)
// place:* => lips | teeth | ridge | palate | velum
// air:*   => burst | noisy | smooth
// sec:*   => palatalized | velarized
// phon:*  => voiced | devoice-final
// hint:*  => less-aspirated-ru   (teaching hint; can affect AIR_BURST intensity)
//
// Added for UI:
// - userComment: learner-facing explanation derived from Husby notes.
// - visVowel: ONLY when the note is about vowel QUALITY (front/central/back, close/mid/open, rounding).
//            If the note is only about LENGTH (shorter/longer), keep visVowel undefined so no diagram shows.

export const RUSSIAN_PACK = {
  meta: {
    code: "ru",
    name: "Russian",
    source: "NTNU NoW – Norwegian speech sounds compared to Russian (Olaf Husby)",
  },

  byIpa: {
    // ---- Consonants (p.2) ----
    p: {
      overlap: "+",
      no: ["park"],
      l1: ["парк"],
      comment: "Less aspirated in RU",
      userComment:
        "Bilabial stop. Lips close and release. Russian /p/ is less aspirated than Norwegian, so the burst of air is weaker.",
      vis: { tags: ["place:lips", "air:burst", "hint:less-aspirated-ru"] },
    },

    b: {
      overlap: "+",
      no: ["boks"],
      l1: ["бокс"],
      comment: "±palatalized, unvoiced in final position",
      userComment:
        "Bilabial voiced stop. Lips close and vocal folds vibrate. It may be slightly “softened” (palatalized), and at the end of a word it can lose voicing.",
      vis: { tags: ["place:lips", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    t: {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "±palatalized, less aspirated in RU",
      userComment:
        "Ridge (alveolar) stop. Tongue touches the ridge behind the teeth. Russian /t/ is less aspirated and may be slightly “softened” (palatalized).",
      vis: { tags: ["place:ridge", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    d: {
      overlap: "~",
      no: ["doctor"],
      l1: ["доктор"],
      comment: "±palatalized, unvoiced in final position",
      userComment:
        "Ridge (alveolar) voiced stop. Tongue touches the ridge and vocal folds vibrate. It may be palatalized and can lose voicing at the end of a word.",
      vis: { tags: ["place:ridge", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    "ʈ": { overlap: "-", no: [], l1: [], comment: "" },
    "ɖ": { overlap: "-", no: [], l1: [], comment: "" },

    k: {
      overlap: "~",
      no: ["kakao"],
      l1: ["какао"],
      comment: "±palatalized, less aspirated in RU",
      userComment:
        "Velar stop with extra palatalization — the back of the tongue closes at the velum, and the tongue body also lifts toward the hard palate. Russian /k/ is less aspirated.",
      vis: { tags: ["place:velum", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    "ɡ": {
      overlap: "~",
      no: ["granat"],
      l1: ["гранат"],
      comment: "±palatalized, unvoiced in final position",
      userComment:
        "Velar voiced stop. Back of tongue closes at the soft palate. It may be palatalized and can lose voicing at the end of a word.",
      vis: { tags: ["place:velum", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    m: {
      overlap: "+",
      no: ["masse"],
      l1: ["масса"],
      comment: "",
      userComment:
        "Bilabial nasal. Lips close but air flows through the nose, so the airflow is smooth and continuous.",
      vis: { tags: ["place:lips", "air:smooth"] },
    },

    n: {
      overlap: "~",
      no: ["NATO"],
      l1: ["нато"],
      comment: "±palatalized",
      userComment:
        "Ridge (alveolar) nasal. Tongue touches the ridge behind the teeth and air flows through the nose. It may also be palatalized (a slightly “softer” version).",
      vis: { tags: ["place:ridge", "air:smooth", "sec:palatalized"] },
    },

    "ɳ": { overlap: "-", no: [], l1: [], comment: "" },
    "ŋ": { overlap: "-", no: [], l1: [], comment: "" },

    f: {
      overlap: "+",
      no: ["kafé"],
      l1: ["кафе"],
      comment: "",
      userComment:
        "Labiodental fricative. Lower lip approaches the teeth and creates noisy airflow.",
      vis: { tags: ["place:teeth", "air:noisy"] },
    },

    "ʋ": {
      overlap: "~",
      no: ["vampyr"],
      l1: ["вампир"],
      comment: "May be unvoiced in final position",
      userComment:
        "Labiodental approximant. Lip approaches the teeth with smooth airflow. It may lose voicing at the end of a word.",
      vis: { tags: ["place:teeth", "air:smooth", "phon:devoice-final"] },
    },

    s: {
      overlap: "+",
      no: ["samovar"],
      l1: ["самовар"],
      comment: "",
      userComment:
        "Ridge (alveolar) fricative. Tongue near the ridge makes a narrow gap and noisy airflow.",
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ʃ": {
      overlap: "+",
      no: ["sjokolade"],
      l1: ["шоколад"],
      comment: "",
      userComment:
        "Fricative a bit further back than /s/. For now shown as ridge with noisy airflow (postalveolar spot is not yet a separate landmark).",
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ç": {
      overlap: "~",
      no: ["kirurg"],
      l1: ["хирург"],
      comment: "Letter <x> before <и>",
      userComment:
        "Palatal fricative. Middle of the tongue lifts toward the hard palate and creates noisy airflow.",
      vis: { tags: ["place:palate", "air:noisy"] },
    },

    j: {
      overlap: "+",
      no: ["jogg"],
      l1: ["йог"],
      comment: "",
      userComment:
        "Palatal glide. Tongue body raised toward the hard palate with smooth airflow.",
      vis: { tags: ["place:palate", "air:smooth"] },
    },

    h: {
      overlap: "~",
      no: ["hockey"],
      l1: ["хоккей"],
      comment: "Before front vowel ~ [ç], back vowel ~ [x]",
      userComment:
        "This fricative shifts place: before front vowels it is palatal; before back vowels it is velar. Both targets are shown.",
      vis: { tags: ["place:palate", "place:velum", "air:noisy"] },
    },

    r: {
      overlap: "+",
      no: ["ris"],
      l1: ["рис"],
      comment: "",
      userComment:
        "Ridge liquid/trill. Tongue makes a quick tap or vibration near the ridge with smooth airflow.",
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    l: {
      overlap: "~",
      no: ["metall"],
      l1: ["металл"],
      comment: "May be velarized, ±palatalized",
      userComment:
        "Ridge lateral. Air flows along the sides of the tongue. It may be velarized (“darker”) or palatalized (“softer”).",
      vis: { tags: ["place:ridge", "air:smooth", "sec:velarized", "sec:palatalized"] },
    },

    "ɭ": { overlap: "-", no: [], l1: [], comment: "" },

    // ---- Vowels / diphthongs ----
    // IMPORTANT:
    // - If note is ONLY length (“shorter/longer”), keep visVowel undefined.
    // - If note is quality (“more central/front/back/open”), add visVowel.

    i: {
      overlap: "+",
      no: ["risk"],
      l1: ["риск"],
      comment: "",
      userComment: "Close front vowel. Russian and Norwegian are very similar here.",
      // quality is not contrasted -> no visVowel
    },

    "iː": {
      overlap: "~",
      no: ["ris"],
      l1: ["рис"],
      comment: "RU vowel is shorter than NO",
      userComment: "Vowel quality is similar, but Russian is typically shorter (duration).",
      // length-only -> no visVowel
    },

    y: {
      overlap: "-",
      no: [],
      l1: [],
      comment: "",
    },

    "yː": {
      overlap: "-",
      no: [],
      l1: [],
      comment: "",
    },

    e: {
      overlap: "~",
      no: ["ekko"],
      l1: ["эхо"],
      comment: "RU vowel is more central: [ə]",
      userComment:
        "Norwegian /e/ is more front. The closest Russian vowel is more central (closer to ‘uh’ in the mouth), not as front as Norwegian /e/.",
      // quality contrast -> show curves
     visVowel: {
    no: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.50 , y: 0.25 },
        end:   { x: 0.73, y: 0.40  },
      },
    },
    l1: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.63 , y: 0.24 },
        end:   { x: 0.74, y: 0.41  },
      },
    },}
    },

    "eː": {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "RU vowel is shorter than NO",
      userComment: "Vowel quality is similar, but Russian is typically shorter (duration).",
      // length-only -> no visVowel
    },

    "ø": { overlap: "-", no: [], l1: [], comment: "" },
    "øː": { overlap: "-", no: [], l1: [], comment: "" },
    "æ": { overlap: "-", no: [], l1: [], comment: "" },
    "æː": { overlap: "-", no: [], l1: [], comment: "" },

    "ɑ": {
      overlap: "+",
      no: ["alfabet"],
      l1: ["алфавит"],
      comment: "RU vowel is more front than NO",
      userComment:
        "Open vowel. Norwegian /ɑ/ is farther back. The closest Russian vowel is more front (tongue a bit more forward).",
      // quality contrast -> show curves
      visVowel: {
    l1: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.50 , y: 0.25 },
        end:   { x: 0.73, y: 0.40  },
      },
    },
    no: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.63 , y: 0.24 },
        end:   { x: 0.74, y: 0.41  },
      },
    }
    },

    },

    "ɑː": {
      overlap: "~",
      no: ["da"],
      l1: ["да"],
      comment: "RU vowel is more front than NO",
      userComment:
        "Similar to /ɑ/, but the closest Russian vowel is still more front than Norwegian.",
       visVowel: {
    l1: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.50 , y: 0.25 },
        end:   { x: 0.73, y: 0.40  },
      },
    },
    no: {
      curve: {
        start: { x: 0.43, y: 0.40 },
        mid:   { x: 0.63 , y: 0.24 },
        end:   { x: 0.74, y: 0.41  },
      },
    }
    },
    },

    "ʉ": { overlap: "-", no: [], l1: [], comment: "" },
    "ʉː": { overlap: "-", no: [], l1: [], comment: "" },

    u: {
      overlap: "+",
      no: ["punkt"],
      l1: ["пункт"],
      comment: "",
      userComment: "Back close rounded vowel. Russian and Norwegian are fairly similar here.",
      // no clear quality note -> no visVowel
    },

    "uː": {
      overlap: "~",
      no: ["ord"],
      l1: ["ур"],
      comment: "RU vowel is shorter than NO",
      userComment: "Vowel quality is similar, but Russian is typically shorter (duration).",
      // length-only -> no visVowel
    },

    o: {
      overlap: "+",
      no: ["koks"],
      l1: ["кокс"],
      comment: "",
      userComment: "Back mid rounded vowel. Similar in both languages.",
      // no visVowel
    },

    "oː": {
      overlap: "~",
      no: ["på"],
      l1: ["по"],
      comment: "RU vowel is shorter than NO",
      userComment: "Vowel quality is similar, but Russian is typically shorter (duration).",
      // length-only -> no visVowel
    },

    // Diphthongs:
    // Right now: keep visVowel off (we can add a “start→end” overlay later if you want).
    "ɑi": {
      overlap: "~",
      no: ["mai"],
      l1: ["май"],
      comment: "NO /ɑ/ is more back",
      userComment:
        "Norwegian starts the diphthong with a more back /ɑ/. Russian starts a bit more forward.",
      // if you want, we can add a diphthong overlay schema later
    },

    "æi": {
      overlap: "~",
      no: ["ei"],
      l1: ["эй"],
      comment: "NO starts more open: /æ/",
      userComment:
        "Norwegian starts more open (jaw more open) than the closest Russian version.",
      // could be visualized later as start-point height difference
    },

    "øy": { overlap: "-", no: [], l1: [], comment: "" },
    "æʉ": { overlap: "-", no: [], l1: [], comment: "" },

    oy: {
      overlap: "+",
      no: ["Roy"],
      l1: ["рой"],
      comment: "RU /o/ is longer than NO",
      userComment: "Main difference is length: Russian /o/ tends to be longer here.",
      // length-only -> no visVowel
    },

    // NOTE: RU example is vulgar; keep for fidelity OR mask it in the UI layer.
    uy: { overlap: "+", no: ["hoi"], l1: ["хуй"], comment: "Rare in NO (NB: RU word is vulgar)" },

    "ʉy": { overlap: "-", no: [], l1: [], comment: "Rare in NO" },
  },
};
